/**
 * Rashi emblem art under `public/rashi-symbols/`.
 * Resolve by slug or any known rashi name (Devanagari, English, Latin).
 */
import { RASHI_CHART_ROW_ENGLISH, RASHI_CHART_ROW_LATIN, RASHI_CHART_ROWS } from './jyotishaRashiChart';

/** Canonical slug → exact PNG basename on disk (case-sensitive). */
export const RASHI_SYMBOL_SLUG_TO_FILE = {
  mesha: 'mesha',
  vrushabh: 'vrushabh',
  mithun: 'mithun',
  kark: 'kark',
  singh: 'singh',
  kanya: 'kanya',
  tula: 'tula',
  vrischika: 'VRISCHIKA',
  dhanu: 'DHANU',
  makar: 'makar',
  kumbh: 'kumbh',
  meena: 'meena',
};

/** Wheel order: Mesha (1) → Meena (12). */
export const RASHI_SYMBOL_SLUGS_ORDERED = Object.keys(RASHI_SYMBOL_SLUG_TO_FILE);

const PUBLIC_BASE = '/rashi-symbols';

/** @type {Record<string, string>} name variant → slug */
const SLUG_BY_NAME = (() => {
  const m = {};
  const add = (name, slug) => {
    if (name == null || name === '') return;
    m[String(name).trim()] = slug;
    const lower = String(name).trim().toLowerCase();
    if (lower) m[lower] = slug;
  };

  RASHI_SYMBOL_SLUGS_ORDERED.forEach((slug) => add(slug, slug));

  RASHI_CHART_ROWS.forEach((row, i) => {
    const slug = RASHI_SYMBOL_SLUGS_ORDERED[i];
    add(row.rashi, slug);
    add(RASHI_CHART_ROW_ENGLISH[i], slug);
    add(RASHI_CHART_ROW_LATIN[i], slug);
  });

  // Common spelling variants
  add('Vrishabha', 'vrushabh');
  add('Vrishabh', 'vrushabh');
  add('Mithuna', 'mithun');
  add('Karka', 'kark');
  add('Simha', 'singh');
  add('Vrishchika', 'vrischika');
  add('Vrischika', 'vrischika');
  add('Makara', 'makar');
  add('Kumbha', 'kumbh');
  add('Meen', 'meena');
  add('Mina', 'meena');
  add('कुम्भ', 'kumbh');

  return m;
})();

/**
 * @param {string} slug
 * @returns {string | null}
 */
export function rashiSymbolImageSrcFromSlug(slug) {
  const file = RASHI_SYMBOL_SLUG_TO_FILE[slug];
  if (!file) return null;
  return `${PUBLIC_BASE}/${file}.png`;
}

/**
 * @param {string} name - Devanagari, English, Latin, or slug
 * @returns {string | null}
 */
export function rashiSymbolImageSrcFromName(name) {
  if (name == null || name === '') return null;
  const key = String(name).trim();
  const slug = SLUG_BY_NAME[key] ?? SLUG_BY_NAME[key.toLowerCase()];
  if (!slug) return null;
  return rashiSymbolImageSrcFromSlug(slug);
}

/**
 * @param {number} index - 1–12 (Mesha–Meena)
 * @returns {string | null}
 */
export function rashiSymbolImageSrcFromIndex(index) {
  const i = Number(index);
  if (!Number.isFinite(i) || i < 1 || i > 12) return null;
  return rashiSymbolImageSrcFromSlug(RASHI_SYMBOL_SLUGS_ORDERED[i - 1]);
}

/**
 * @param {{ slug?: string, hi?: string, en?: string, latin?: string, index?: number } | string | number} input
 * @returns {string | null}
 */
export function rashiSymbolImageSrc(input) {
  if (input == null) return null;

  if (typeof input === 'number' || (typeof input === 'string' && /^\d+$/.test(input.trim()))) {
    return rashiSymbolImageSrcFromIndex(Number(input));
  }

  if (typeof input === 'string') {
    return rashiSymbolImageSrcFromName(input) ?? rashiSymbolImageSrcFromSlug(input);
  }

  const { slug, hi, en, latin, index } = input;
  if (slug) {
    const bySlug = rashiSymbolImageSrcFromSlug(slug);
    if (bySlug) return bySlug;
  }
  for (const name of [hi, en, latin]) {
    const src = rashiSymbolImageSrcFromName(name);
    if (src) return src;
  }
  if (index != null) return rashiSymbolImageSrcFromIndex(index);
  return null;
}
